import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AccountInfo } from '@azure/msal-browser';
import { msalInstance, apiScope } from './msalConfig.js';
import { reportClientError } from '../telemetry/reportClientError.js';

interface AuthContextValue {
  account: AccountInfo | null;
  isInitializing: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  account: null,
  isInitializing: true,
  signIn: async () => {
    /* no-op until provider mounts */
  },
  signOut: async () => {
    /* no-op until provider mounts */
  },
});

export function AuthProvider({ children }: PropsWithChildren) {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    async function init(): Promise<void> {
      try {
        await msalInstance.initialize();
        const result = await msalInstance.handleRedirectPromise();
        const acct =
          result?.account ?? msalInstance.getAllAccounts()[0] ?? null;
        msalInstance.setActiveAccount(acct);
        setAccount(acct);
      } catch (err) {
        // console.error first, and not merely as a duplicate: /v1/client-errors
        // requires a bearer token, so an MSAL *init* failure has no session to
        // authenticate with and reportClientError will drop it. The report call
        // below only reaches the BFF for later failures (e.g. a silent refresh
        // after a successful login). It also depends on index.tsx having called
        // setClientErrorTokenProvider — mounting this provider outside that
        // bootstrap (in a test, say) leaves the reporter with no token source.
        // Both are by design; see telemetry/reportClientError.ts.
        console.error('[MSAL] init error', err);
        reportClientError(
          'msal_error',
          err instanceof Error ? err.message : String(err),
          err instanceof Error ? err.stack : undefined,
        );
      } finally {
        setIsInitializing(false);
      }
    }
    void init();
  }, []);

  async function signIn(): Promise<void> {
    await msalInstance.loginRedirect({ scopes: apiScope });
  }

  async function signOut(): Promise<void> {
    // logoutRedirect clears the sessionStorage MSAL cache and navigates away.
    await msalInstance.logoutRedirect();
  }

  return (
    <AuthContext.Provider value={{ account, isInitializing, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
